import type {
  AgentRunComparison,
  AgentRunDetail,
  MatchSide,
  MatchSideComparison,
} from '~/entities/document-agent'
import { REVIEWED_STATUSES } from '~/entities/document-agent'

/**
 * How a value compares to the document's final state. `pending`: the final state is not settled
 * yet; `none`: there is nothing to compare (no value, or the field does not apply).
 */
export type Agreement = 'agree' | 'partial' | 'differ' | 'pending' | 'none'

export type CategoryParts = { phase: string | null; outcome: string | null; reason: string | null }

export const CATEGORY_PART_KEYS = ['phase', 'outcome', 'reason'] as const

export type CategoryPartKey = (typeof CATEGORY_PART_KEYS)[number]

export function isReviewed(comparison: Pick<AgentRunComparison, 'review_status'> | null): boolean {
  return REVIEWED_STATUSES.has(comparison?.review_status ?? '')
}

/** A category against the final one; partial when some of phase, outcome and reason agree. */
export function compareCategory({
  value,
  parts,
  final,
  finalParts,
  reviewed,
}: {
  value: string | null
  parts: CategoryParts | null
  final: string | null
  finalParts: CategoryParts | null
  reviewed: boolean
}): Agreement {
  if (!value) return 'none'
  if (!reviewed || !final) return 'pending'
  if (value === final) return 'agree'
  if (!parts || !finalParts) return 'differ'
  // Two missing parts are not an agreement: only parts both categories have count.
  const shared = CATEGORY_PART_KEYS.some(
    (key) => parts[key] !== null && parts[key] === finalParts[key],
  )
  return shared ? 'partial' : 'differ'
}

/** Which parts of a category agree with the final one, for coloring them one by one. */
export function comparePartsToFinal(
  parts: CategoryParts | null,
  finalParts: CategoryParts | null,
  reviewed: boolean,
): Record<CategoryPartKey, Agreement> {
  return Object.fromEntries(
    CATEGORY_PART_KEYS.map((key) => {
      if (!parts?.[key] && !finalParts?.[key]) return [key, 'none']
      if (!reviewed || !finalParts) return [key, 'pending']
      return [key, parts?.[key] === finalParts[key] ? 'agree' : 'differ']
    }),
  ) as Record<CategoryPartKey, Agreement>
}

export type CodeStatus = 'agreed' | 'missed' | 'extra' | 'unscored'

export type CodeRow = {
  code: string
  inFinal: boolean
  inAgent: boolean
  inToday: boolean
  /** The agent's code against the final ones. */
  agent: CodeStatus
  /** Production's code against the final ones. */
  today: CodeStatus
}

export type CodeTally = { agreed: number; missed: number; extra: number }

function codeStatus(inFinal: boolean, inValue: boolean, hasFinal: boolean): CodeStatus {
  if (!hasFinal) return 'unscored'
  if (inFinal && inValue) return 'agreed'
  if (inFinal) return 'missed'
  return inValue ? 'extra' : 'agreed'
}

/** Every code any of the three has: the final ones first, then the agent's, then production's. */
export function buildCodeRows(
  finalCodes: string[] | null,
  agentCodes: string[],
  todayCodes: string[],
): CodeRow[] {
  const hasFinal = finalCodes !== null
  const finalSet = new Set(finalCodes ?? [])
  const agentSet = new Set(agentCodes)
  const todaySet = new Set(todayCodes)
  const order = [
    ...[...finalSet].sort(),
    ...[...agentSet].filter((code) => !finalSet.has(code)).sort(),
    ...[...todaySet].filter((code) => !finalSet.has(code) && !agentSet.has(code)).sort(),
  ]
  return order.map((code) => ({
    code,
    inFinal: finalSet.has(code),
    inAgent: agentSet.has(code),
    inToday: todaySet.has(code),
    agent: codeStatus(finalSet.has(code), agentSet.has(code), hasFinal),
    today: codeStatus(finalSet.has(code), todaySet.has(code), hasFinal),
  }))
}

export function tallyCodes(rows: CodeRow[], who: 'agent' | 'today'): CodeTally {
  const present = (row: CodeRow) => (who === 'agent' ? row.inAgent : row.inToday)
  return {
    agreed: rows.filter((row) => row.inFinal && present(row)).length,
    missed: rows.filter((row) => row.inFinal && !present(row)).length,
    extra: rows.filter((row) => !row.inFinal && present(row)).length,
  }
}

export function compareCodes(tally: CodeTally, hasFinal: boolean): Agreement {
  if (!hasFinal) return 'pending'
  if (tally.missed === 0 && tally.extra === 0) return 'agree'
  return tally.agreed > 0 ? 'partial' : 'differ'
}

/** The server's verdict on the agent's pick, as an agreement. */
export function agentMatchAgreement(verdict: string | null | undefined): Agreement {
  switch (verdict) {
    case 'correct':
      return 'agree'
    case 'in_tie':
      return 'partial'
    case 'wrong':
    case 'no_pick':
      return 'differ'
    case 'pending':
      return 'pending'
    default:
      return 'none'
  }
}

/**
 * Production's arrival rule against the final case, read from the truth outcome the server
 * computed (`match_truth.outcome`); the UI never compares case ids itself.
 */
export function productionMatchAgreement(outcome: string | null | undefined): Agreement {
  if (!outcome) return 'none'
  if (outcome.startsWith('rule_auto:') || outcome.startsWith('rule_confirmed:')) return 'agree'
  if (outcome === 'rule_overridden' || outcome === 'rule_rejected') return 'differ'
  // The rule abstained: right when there was nothing among its candidates (no match, or a case
  // the reviewer had to add by hand), wrong when the case was among them (top, lower).
  if (outcome === 'no_match' || outcome === 'hand_added') return 'agree'
  if (outcome === 'top' || outcome === 'lower') return 'differ'
  if (outcome === 'pending') return 'pending'
  return 'none'
}

/** Production's rule picked nothing on arrival, and a reviewer had to find the case. */
export function isProductionAbstained(outcome: string | null | undefined): boolean {
  return outcome === 'top' || outcome === 'lower' || outcome === 'hand_added'
}

/** How the final case was settled (`match_truth.final_case` sources), in a few words. */
export function describeFinalCaseSource(source: string | null | undefined): string | null {
  switch (source) {
    case null:
    case undefined:
    case '':
      return null
    case 'validated':
      return 'Validated by a reviewer'
    case 'no_match':
      return 'A reviewer found no matching case'
    case 'auto_reviewed':
      return 'Auto-reviewed on a rule match'
    default:
      return source.replace(/_/g, ' ')
  }
}

/** What the truth outcome says about production, in a few words. */
export function describeProductionOutcome(outcome: string | null | undefined): string | null {
  if (!outcome) return null
  const [kind, method] = outcome.split(':')
  switch (kind) {
    case 'rule_auto':
      return `Auto-matched by the ${method} rule; the document was auto-reviewed on it`
    case 'rule_confirmed':
      return `The ${method} rule's pick was confirmed by the reviewer`
    case 'rule_overridden':
      return "The reviewer chose a different case than the rule's pick"
    case 'rule_rejected':
      return "The rule picked a case; the reviewer said there's no match"
    case 'no_match':
      return 'The rule abstained, and the reviewer found no match'
    case 'top':
      return 'The rule abstained; the reviewer chose the top candidate'
    case 'lower':
      return 'The rule abstained; the reviewer chose a lower candidate'
    case 'hand_added':
      return 'The rule abstained; the reviewer added a case not among the candidates'
    case 'no_lookup':
      return 'Production did not look up cases on this side'
    case 'pending':
      return 'Not reviewed yet'
    case 'unresolved':
      return "The reviewer's final case could not be resolved"
    default:
      return outcome
  }
}

/** The side the match verdict is scored on, and its comparison. */
export function getScoredSide(
  comparison: AgentRunComparison | null,
): { side: MatchSide; data: MatchSideComparison } | null {
  const matching = comparison?.matching
  const side = matching?.final_side
  if (!matching || !side || matching.excluded) return null
  return { side, data: matching.sides[side] }
}

export function getAgentCategoryParts(run: AgentRunDetail): CategoryParts | null {
  const final = run.final
  if (!final || !final.phase) return null
  // The agent writes a missing outcome or reason as "none"; `/categories` metadata leaves it out.
  const part = (value: string | null) => (value && value !== 'none' ? value : null)
  return { phase: part(final.phase), outcome: part(final.outcome), reason: part(final.reason) }
}

/** The worst of several agreements, for a one-word overall verdict. */
export function combineAgreements(agreements: Agreement[]): Agreement {
  const scored = agreements.filter((value) => value !== 'none')
  if (scored.length === 0) return 'none'
  if (scored.every((value) => value === 'pending')) return 'pending'
  const settled = scored.filter((value) => value !== 'pending')
  if (settled.every((value) => value === 'agree')) return 'agree'
  if (settled.every((value) => value === 'differ')) return 'differ'
  return 'partial'
}
